/**
 * "Open in Nanome": the REST sequence of docs/PLAN.md (port of vendor-ref/open-in-nanome-2/pipeline.js:1188-1426
 * importSession, minus the v1 converter and annotations):
 *
 *  0. GET {mara}/api/info                                  workspace_api_url, accounts_api_url
 *  1. GET {accounts}/user/session?slim=true                 who, and which license (401 -> AuthExpired)
 *  2. pre-flight                                            free license: 3 owned workspaces, 5 entries (License.cs:13-15);
 *                                                           formats the server cannot load (converters.ts)
 *  3. POST {ws}/workspaces                                  one scene comes with it (CreateWorkspace.cs:88-92)
 *  4. POST {ws}/workspaces/{id}/entries/load-multiple       ONE file per request; serials from the answer
 *  5. GET  {ws}/workspaces/{id}/scenes                      lowest serial
 *  6. per entry: POST .../entries/{e}/components/add-default when every Lite component of it is untouched,
 *                else POST .../components/add per component, then GET .../components + toggle-hidden
 *  7. POST .../entries/{e}/model-serial                     entries not on model 1
 *  8. POST {ws}/workspaces/{id}/set-public                  optional
 *  9. {mara}/workspaces/{id}                                the URL to open
 *
 * Progress is persisted in sessionStorage after every server write, so a rerun with the same input (same files,
 * same component list) resumes at the failed step instead of creating a second workspace. On an unrecoverable
 * failure after step 3 the error carries `partial` and the UI can call cleanupPartial(workspaceId).
 */
import type { Component, ComponentInput, Entry } from '@nanome-lite/state';
import { filterEntrySerials } from '@nanome-lite/state';
import type { NanomeModel } from '@nanome-lite/data';
import { DEFAULT_MARA_URL, getMaraInfo, trimSlash, type FetchLike } from './discovery';
import { ApiError, AuthExpired, BridgeError, PreflightError, type PipelineStep, type PreflightDetails } from './errors';
import { componentsForServer } from './filters-for-server';
import { prepareUpload, type PreparedUpload } from './converters';
import { getSchemaValidator, type ComponentValidator } from './schema-validator';
import { PERMISSION_OWNER, createWorkspaceApi, type SceneComponentModel, type WorkspaceApi } from './workspace-api';

/** Free license limits (Nanome.Domain/Users/License.cs:13-15). */
export const FREE_MAX_OWNED_WORKSPACES = 3;
export const FREE_MAX_WORKSPACE_ENTRIES = 5;

export type ProgressFn = (step: PipelineStep, message: string, index?: number, total?: number) => void;

export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export interface OpenInNanomeInput {
  token: string;
  /** MARA origin; default https://app.nanome.ai. */
  maraUrl?: string;
  entries: Entry[];
  /** local entry serial -> NanomeModel (needed for mol2 conversion and free-form selections). */
  models: Map<number, NanomeModel>;
  components: Component[];
  makePublic?: boolean;
  workspaceName?: string;
  workspaceDescription?: string;
  onProgress?: ProgressFn;
  signal?: AbortSignal;
  /** Validate every ComponentInput against GET {ws}/schemas and degrade that entry to add-default on failure. Default true. */
  validateSchemas?: boolean;
  /** Resume from persisted progress when the input matches. Default true. */
  resume?: boolean;
  /** Where progress is persisted; default sessionStorage (memory when unavailable). */
  storage?: StorageLike;
  /** Where Lite-created workspace ids are remembered for the LICENSE_ERROR dialog; default localStorage. */
  liteWorkspaceStorage?: StorageLike;
  fetch?: FetchLike;
  timeoutMs?: number;
  uploadTimeoutMs?: number;
  retryDelaysMs?: number[];
  sleep?: (ms: number) => Promise<void>;
}

export interface UploadedEntry {
  localSerial: number;
  serverSerial: number;
  name: string;
  uploadedAs: string;
  converted: boolean;
  /** Model GUIDs the server made (one per model/frame). */
  models: number;
}

export interface OpenInNanomeResult {
  workspaceId: string;
  /** {mara}/workspaces/{id}. */
  url: string;
  /** Share code (POST /workspaces/lookup resolves it; {mara}/join/{code} needs login). */
  code: string;
  /** local entry serial -> server entry serial. */
  entrySerials: Map<number, number>;
  entries: UploadedEntry[];
  sceneSerial: number;
  /** Components sent through components/add (add-default components are not counted). */
  componentCount: number;
  hiddenCount: number;
  warnings: string[];
  resumed: boolean;
}

// --- persisted progress --------------------------------------------------------------------------------------------

export const PROGRESS_KEY = 'nanome-lite.oin.progress';
export const LITE_WORKSPACES_KEY = 'nanome-lite.oin.workspaces';

export interface PipelineProgress {
  version: 1;
  fingerprint: string;
  maraUrl: string;
  workspaceApiUrl: string;
  workspaceId: string;
  url: string;
  code: string;
  sceneSerial?: number;
  /** local serial -> server serial, for entries already loaded. */
  entrySerials: Record<string, number>;
  entryInfo: Record<string, { name: string; uploadedAs: string; converted: boolean; models: number }>;
  /** local serials whose components are in the scene. */
  entriesDone: Record<string, true>;
  crossDone?: boolean;
  framesDone: Record<string, true>;
  publicDone?: boolean;
  step: PipelineStep;
  componentCount: number;
  hiddenCount: number;
  startedAt: number;
}

const memoryStorage = (): StorageLike => {
  const m = new Map<string, string>();
  return { getItem: k => m.get(k) ?? null, setItem: (k, v) => { m.set(k, v); }, removeItem: k => { m.delete(k); } };
};

function defaultStorage(name: 'sessionStorage' | 'localStorage'): StorageLike {
  try {
    const s = (globalThis as unknown as Record<string, StorageLike | undefined>)[name];
    if (s && typeof s.getItem === 'function') return s;
  } catch { /* access denied */ }
  return memoryStorage();
}

export function readProgress(storage: StorageLike = defaultStorage('sessionStorage')): PipelineProgress | null {
  try {
    const raw = storage.getItem(PROGRESS_KEY);
    if (!raw) return null;
    const p = JSON.parse(raw) as PipelineProgress;
    return p && p.version === 1 && typeof p.workspaceId === 'string' ? p : null;
  } catch { return null; }
}

export function clearProgress(storage: StorageLike = defaultStorage('sessionStorage')): void {
  try { storage.removeItem(PROGRESS_KEY); } catch { /* ignore */ }
}

function saveProgress(storage: StorageLike, p: PipelineProgress): void {
  try { storage.setItem(PROGRESS_KEY, JSON.stringify(p)); } catch { /* quota or privacy mode: resume is best-effort */ }
}

export function liteWorkspaceIds(storage: StorageLike = defaultStorage('localStorage')): string[] {
  try {
    const raw = storage.getItem(LITE_WORKSPACES_KEY);
    const ids = raw ? JSON.parse(raw) : [];
    return Array.isArray(ids) ? ids.filter((x): x is string => typeof x === 'string') : [];
  } catch { return []; }
}

export function rememberLiteWorkspace(id: string, storage: StorageLike = defaultStorage('localStorage')): void {
  try {
    const ids = liteWorkspaceIds(storage).filter(x => x !== id);
    ids.push(id);
    storage.setItem(LITE_WORKSPACES_KEY, JSON.stringify(ids));
  } catch { /* ignore */ }
}

export function forgetLiteWorkspace(id: string, storage: StorageLike = defaultStorage('localStorage')): void {
  try { storage.setItem(LITE_WORKSPACES_KEY, JSON.stringify(liteWorkspaceIds(storage).filter(x => x !== id))); } catch { /* ignore */ }
}

/** FNV-1a over what identifies a run: entry serials, file names and sizes, component count. Same input -> same fingerprint. */
export function inputFingerprint(entries: readonly Entry[], components: readonly Component[]): string {
  const parts = entries.map(e => `${e.serial}:${e.source.fileName}:${e.source.bytes.length}:${e.currentModelSerial}`);
  parts.push(`c:${components.length}:${components.map(c => `${c.serial}${c.hidden ? 'h' : ''}${c.isDefault ? 'd' : ''}`).join(',')}`);
  let h = 0x811c9dc5;
  for (const ch of parts.join('|')) { h ^= ch.charCodeAt(0); h = Math.imul(h, 0x01000193) >>> 0; }
  return `${h.toString(16).padStart(8, '0')}-${entries.length}`;
}

// --- helpers ---------------------------------------------------------------------------------------------------------

/**
 * The serials the server gave the components just added (`sent`, in add order). A new component is numbered one past
 * the scene's highest serial (Scene.cs AddComponent), but the list comes in no set order: the ones not listed before
 * the adds, sorted by serial, are the ones sent, and their names must say so (pipeline.js:1173-1186).
 */
export function addedComponentSerials(before: readonly SceneComponentModel[], after: readonly SceneComponentModel[], sent: readonly ComponentInput[]): number[] {
  const known = new Set(before.map(c => c.serial));
  const added = after.filter(c => !known.has(c.serial)).sort((a, b) => a.serial - b.serial);
  if (added.length !== sent.length) {
    throw new BridgeError('hidden', `the scene lists ${added.length} new component(s) after ${sent.length} were added, so the ones to hide cannot be told apart`);
  }
  const wrong = added.findIndex((c, i) => c.name !== sent[i]!.name);
  if (wrong >= 0) {
    throw new BridgeError('hidden', `the scene's new components, in serial order, are not the ones added: #${wrong + 1} is "${added[wrong]!.name}" (serial ${added[wrong]!.serial}), "${sent[wrong]!.name}" was added`);
  }
  return added.map(c => c.serial);
}

const componentEntrySerials = (c: Component): number[] => c.entrySerials?.length ? c.entrySerials : filterEntrySerials(c.filters);

export function workspaceUrl(maraUrl: string, workspaceId: string): string {
  return `${trimSlash(maraUrl)}/workspaces/${encodeURIComponent(workspaceId)}`;
}

function defaultWorkspaceName(entries: readonly Entry[]): string {
  const names = entries.map(e => e.name);
  const head = names.slice(0, 3).join(', ');
  return names.length > 3 ? `${head} +${names.length - 3}` : head || 'Nanome Lite';
}

// --- the pipeline ----------------------------------------------------------------------------------------------------

export async function openInNanome(input: OpenInNanomeInput): Promise<OpenInNanomeResult> {
  const storage = input.storage ?? defaultStorage('sessionStorage');
  const liteStorage = input.liteWorkspaceStorage ?? defaultStorage('localStorage');
  const maraUrl = trimSlash(input.maraUrl ?? DEFAULT_MARA_URL);
  const warnings: string[] = [];
  let step: PipelineStep = 'info';
  const state: { progress: PipelineProgress | null } = { progress: null };
  const report = (s: PipelineStep, message: string, index?: number, total?: number) => {
    step = s;
    try { input.onProgress?.(s, message, index, total); } catch { /* a broken progress display must not break the run (pipeline.js:1075-1078) */ }
  };

  try {
    return await run();
  } catch (e) {
    const err = e instanceof BridgeError ? e : new BridgeError(step, `${step}: ${(e as Error)?.message ?? String(e)}`, { cause: e });
    const partial = state.progress;
    if (partial?.workspaceId) {
      err.partial = { workspaceId: partial.workspaceId, url: partial.url };
      if (!(err instanceof AuthExpired)) err.message += ` (the workspace was created and left as it is: ${partial.url}; rerun to resume, or delete it)`;
    }
    throw err;
  }

  async function run(): Promise<OpenInNanomeResult> {
    if (!input.token) throw new BridgeError('session', 'session: no Nanome token');
    const fingerprint = inputFingerprint(input.entries, input.components);

    // 0. Discovery.
    report('info', `Reading ${maraUrl}/api/info`);
    const infoOpts: Parameters<typeof getMaraInfo>[1] = {};
    if (input.fetch) infoOpts.fetch = input.fetch;
    if (input.signal) infoOpts.signal = input.signal;
    const info = await getMaraInfo(maraUrl, infoOpts);
    const api = createWorkspaceApi({
      baseUrl: info.workspace_api_url, token: input.token,
      ...(input.fetch ? { fetch: input.fetch } : {}),
      ...(input.signal ? { signal: input.signal } : {}),
      ...(input.timeoutMs !== undefined ? { timeoutMs: input.timeoutMs } : {}),
      ...(input.uploadTimeoutMs !== undefined ? { uploadTimeoutMs: input.uploadTimeoutMs } : {}),
      ...(input.retryDelaysMs ? { retryDelaysMs: input.retryDelaysMs } : {}),
      ...(input.sleep ? { sleep: input.sleep } : {}),
    });

    // 1. Session and license.
    report('session', 'Checking the Nanome session');
    const session = await api.getSession(info.accounts_api_url);

    // Resume?
    const saved = input.resume === false ? null : readProgress(storage);
    const resumed = !!saved && saved.fingerprint === fingerprint && saved.workspaceApiUrl === info.workspace_api_url;
    if (saved && !resumed) clearProgress(storage);
    if (resumed) state.progress = saved;

    // 2. Pre-flight: nothing is created before this passes.
    report('preflight', 'Checking license limits and file formats');
    if (!input.entries.length) throw new PreflightError('Nothing to send: load a structure first.', { reason: 'no-entries' });
    const uploads = new Map<number, PreparedUpload>();
    const unsupported: NonNullable<PreflightDetails['entries']> = [];
    for (const e of input.entries) {
      const u = prepareUpload(e, input.models.get(e.serial));
      uploads.set(e.serial, u);
      if (u.note && !u.converted && !u.fallbackUrl) unsupported.push({ serial: e.serial, name: e.name, fileName: e.source.fileName, note: u.note });
      else if (u.note) warnings.push(u.note);
    }
    if (unsupported.length) {
      throw new PreflightError(`${unsupported.length} file(s) cannot be sent to Nanome: ${unsupported.map(u => u.fileName).join(', ')}`, { reason: 'unsupported-format', entries: unsupported });
    }
    const free = session.licenseType === 'free';
    if (free && input.entries.length > FREE_MAX_WORKSPACE_ENTRIES) {
      throw new PreflightError(`A free Nanome license holds at most ${FREE_MAX_WORKSPACE_ENTRIES} structures per workspace; ${input.entries.length} are loaded.`, {
        reason: 'license-entries', licenseType: 'free', entryCount: input.entries.length, maxEntries: FREE_MAX_WORKSPACE_ENTRIES,
        entries: input.entries.map(e => ({ serial: e.serial, name: e.name, fileName: e.source.fileName })),
      });
    }
    if (free && !state.progress?.workspaceId) {
      const owned = await countOwnedWorkspaces(api, session.user.name, warnings);
      if (owned !== null && owned >= FREE_MAX_OWNED_WORKSPACES) {
        throw new PreflightError(`A free Nanome license owns at most ${FREE_MAX_OWNED_WORKSPACES} workspaces; this account already owns ${owned}. Delete one on app.nanome.ai or upgrade.`, {
          reason: 'license-workspaces', licenseType: 'free', ownedWorkspaces: owned, maxOwnedWorkspaces: FREE_MAX_OWNED_WORKSPACES, liteWorkspaceIds: liteWorkspaceIds(liteStorage),
        });
      }
    }

    // 2b. Fetch mmCIF for bcif entries.
    report('prepare', 'Preparing files');
    for (const e of input.entries) {
      const u = uploads.get(e.serial)!;
      if (!u.fallbackUrl) continue;
      if (state.progress?.entrySerials[String(e.serial)] !== undefined) continue; // already loaded in a previous run
      report('prepare', `Fetching ${u.fallbackUrl}`);
      const bytes = await fetchBytes(u.fallbackUrl, input.fetch, input.signal);
      uploads.set(e.serial, { name: `${e.name}.cif`, bytes, converted: true, note: u.note ?? '' });
    }

    // 3. Workspace.
    if (!state.progress?.workspaceId) {
      const name = input.workspaceName || defaultWorkspaceName(input.entries);
      report('workspace', `Creating workspace "${name}"`);
      const description = input.workspaceDescription ?? `Sent from Nanome Lite: ${input.entries.map(e => e.source.fileName).join(', ')}.`;
      const w = await api.createWorkspace({ name, description });
      state.progress = {
        version: 1, fingerprint, maraUrl, workspaceApiUrl: info.workspace_api_url, workspaceId: w.id, url: workspaceUrl(maraUrl, w.id), code: w.code ?? '',
        entrySerials: {}, entryInfo: {}, entriesDone: {}, framesDone: {}, step: 'load', componentCount: 0, hiddenCount: 0, startedAt: Date.now(),
      };
      saveProgress(storage, state.progress);
      rememberLiteWorkspace(w.id, liteStorage);
    } else {
      report('workspace', `Resuming workspace ${state.progress.url}`);
    }
    const p = state.progress;
    const wsId = p.workspaceId;

    // 4. Entries, one file per request.
    const serialMap = new Map<number, number>();
    const total = input.entries.length;
    for (const [i, e] of input.entries.entries()) {
      const key = String(e.serial);
      const u = uploads.get(e.serial)!;
      if (p.entrySerials[key] !== undefined) { serialMap.set(e.serial, p.entrySerials[key]!); continue; }
      p.step = 'load';
      report('load', `Loading ${i + 1}/${total}: ${u.name}`, i + 1, total);
      const where = `structure file ${i + 1} of ${total}, ${u.name}`;
      const loaded = await api.loadEntries(wsId, [{ name: u.name, bytes: u.bytes }]);
      if (loaded.length !== 1) throw new ApiError('load', `the Workspace API created ${loaded.length} entries for one file, so they cannot be matched to it (${where})`, { status: 200, body: JSON.stringify(loaded), url: `${api.baseUrl}/workspaces/${wsId}/entries/load-multiple`, method: 'POST' });
      const entry = loaded[0]!;
      if (!Number.isInteger(entry.serial)) throw new ApiError('load', `the entry came back without a serial (${where})`, { status: 200, body: JSON.stringify(entry), url: `${api.baseUrl}/workspaces/${wsId}/entries/load-multiple`, method: 'POST' });
      if ([...serialMap.values()].includes(entry.serial)) throw new ApiError('load', `entry serial ${entry.serial} came back a second time (${where})`, { status: 200, body: JSON.stringify(entry), url: `${api.baseUrl}/workspaces/${wsId}/entries/load-multiple`, method: 'POST' });
      serialMap.set(e.serial, entry.serial);
      p.entrySerials[key] = entry.serial;
      p.entryInfo[key] = { name: entry.name, uploadedAs: u.name, converted: u.converted, models: Array.isArray(entry.models) ? entry.models.length : 0 };
      saveProgress(storage, p);
      const sentName = u.name.replace(/\.gz$/i, '').replace(/\.[^.]*$/, '');
      if (entry.name !== sentName) warnings.push(`Entry ${entry.serial} came back named "${entry.name}"; it was sent as "${sentName}".`);
    }

    // 5. Scene.
    if (p.sceneSerial === undefined) {
      report('scene', 'Reading the scene');
      const scenes = await api.listScenes(wsId);
      const scene = scenes.length ? scenes.reduce((a, b) => (b.serial < a.serial ? b : a)) : null;
      if (!scene || !Number.isInteger(scene.serial)) throw new ApiError('scene', 'the new workspace has no scene', { status: 200, body: JSON.stringify(scenes), url: `${api.baseUrl}/workspaces/${wsId}/scenes`, method: 'GET' });
      p.sceneSerial = scene.serial;
      saveProgress(storage, p);
    }
    const sceneSerial = p.sceneSerial;

    // 6. Components.
    let validator: ComponentValidator | null = null;
    if (input.validateSchemas !== false) {
      try { validator = await getSchemaValidator(api.baseUrl, () => api.getSchemas()).ready(); }
      catch (e) { warnings.push(`Payloads were not validated: GET ${api.baseUrl}/schemas failed (${(e as Error)?.message ?? e}).`); }
    }
    const byEntry = new Map<number, Component[]>();
    const cross: Component[] = [];
    for (const c of input.components) {
      const es = componentEntrySerials(c);
      if (es.length === 1) { const list = byEntry.get(es[0]!) ?? []; list.push(c); byEntry.set(es[0]!, list); }
      else cross.push(c);
    }
    const addGranular = async (comps: Component[], label: string): Promise<boolean> => {
      const { send, hiddenIndexes, warnings: w } = componentsForServer(comps, input.entries, serialMap, input.models);
      warnings.push(...w);
      if (validator) {
        for (const c of send) {
          const v = validator(c);
          if (!v.ok) { warnings.push(`Component "${c.name}" does not match the server schema (${v.errors.slice(0, 3).join('; ')}).`); return false; }
        }
      }
      if (!send.length) return true;
      const before = hiddenIndexes.length ? await api.getComponents(wsId, sceneSerial) : [];
      for (const [i, c] of send.entries()) {
        p.step = 'components';
        report('components', `Adding ${label} ${i + 1}/${send.length}: ${c.name}`, i + 1, send.length);
        await api.addComponent(wsId, sceneSerial, c);
      }
      p.componentCount += send.length;
      if (hiddenIndexes.length) {
        p.step = 'hidden';
        report('hidden', `Hiding ${hiddenIndexes.length} component(s)`);
        const after = await api.getComponents(wsId, sceneSerial);
        const added = addedComponentSerials(before, after, send);
        await api.toggleHidden(wsId, sceneSerial, hiddenIndexes.map(i => added[i]!), true);
        p.hiddenCount += hiddenIndexes.length;
      }
      return true;
    };
    for (const e of input.entries) {
      const key = String(e.serial);
      if (p.entriesDone[key]) continue;
      const comps = byEntry.get(e.serial) ?? [];
      const serverSerial = serialMap.get(e.serial)!;
      if (comps.length && comps.every(c => c.isDefault)) {
        p.step = 'components';
        report('components', `Adding default components for ${e.name}`);
        await api.addDefaultComponents(wsId, sceneSerial, serverSerial);
      } else if (comps.length) {
        const ok = await addGranular(comps, `component of ${e.name}`);
        if (!ok) {
          warnings.push(`${e.name}: sent with the server's default components instead of Lite's.`);
          report('components', `Adding default components for ${e.name}`);
          await api.addDefaultComponents(wsId, sceneSerial, serverSerial);
        }
      }
      p.entriesDone[key] = true;
      saveProgress(storage, p);
    }
    if (cross.length && !p.crossDone) {
      const ok = await addGranular(cross, 'component');
      if (!ok) warnings.push(`${cross.length} component(s) spanning several entries were not sent (schema mismatch).`);
      p.crossDone = true;
      saveProgress(storage, p);
    }

    // 7. Frames.
    for (const e of input.entries) {
      const key = String(e.serial);
      if (e.currentModelSerial <= 1 || p.framesDone[key]) continue;
      const serverModels = p.entryInfo[key]?.models ?? 0;
      if (serverModels && e.currentModelSerial > serverModels) {
        warnings.push(`${e.name}: Lite shows model ${e.currentModelSerial}, but the server made ${serverModels} model(s); left on the first.`);
        p.framesDone[key] = true; saveProgress(storage, p);
        continue;
      }
      p.step = 'frames';
      report('frames', `Opening ${e.name} on model ${e.currentModelSerial}`);
      await api.setModelSerial(wsId, sceneSerial, serialMap.get(e.serial)!, e.currentModelSerial);
      p.framesDone[key] = true;
      saveProgress(storage, p);
    }

    // 8. Public link.
    if (input.makePublic && !p.publicDone) {
      p.step = 'public';
      report('public', 'Making the workspace public');
      await api.setPublic(wsId, true);
      p.publicDone = true;
      saveProgress(storage, p);
    }

    // 9. Done.
    clearProgress(storage);
    report('done', `Workspace ready: ${p.url}`);
    return {
      workspaceId: wsId, url: p.url, code: p.code, entrySerials: serialMap,
      entries: input.entries.map(e => {
        const i = p.entryInfo[String(e.serial)];
        return { localSerial: e.serial, serverSerial: serialMap.get(e.serial)!, name: i?.name ?? e.name, uploadedAs: i?.uploadedAs ?? e.source.fileName, converted: i?.converted ?? false, models: i?.models ?? 0 };
      }),
      sceneSerial, componentCount: p.componentCount, hiddenCount: p.hiddenCount, warnings, resumed,
    };
  }
}

/** Owned workspaces per CheckCanOwnAnotherWorkspace (LicenseHelper.cs:39-55): permissions at Owner level on a workspace. */
async function countOwnedWorkspaces(api: WorkspaceApi, userName: string, warnings: string[]): Promise<number | null> {
  try {
    const perms = await api.getOwnPermissions({ step: 'preflight' });
    return perms.filter(x => x.workspaceId && x.permissionLevel === PERMISSION_OWNER).length;
  } catch (e) {
    if (e instanceof AuthExpired) throw e;
    if (e instanceof ApiError && e.isAborted) throw e;
  }
  try {
    const list = await api.listWorkspaces({ step: 'preflight' });
    return list.filter(w => w.ownerName === userName).length;
  } catch (e) {
    if (e instanceof AuthExpired) throw e;
    if (e instanceof ApiError && e.isAborted) throw e;
    warnings.push('Could not count owned workspaces before starting; a free license may hit LICENSE_ERROR.');
    return null;
  }
}

async function fetchBytes(url: string, fetchImpl: FetchLike | undefined, signal: AbortSignal | undefined): Promise<Uint8Array> {
  const f: FetchLike = fetchImpl ?? ((i, init) => globalThis.fetch(i, init));
  let res: Response;
  try { res = await f(url, { method: 'GET', signal: signal ?? null }); }
  catch (e) { throw new ApiError('prepare', `GET ${url} failed: ${(e as Error)?.message ?? e}`, { status: 0, url, method: 'GET', code: 'NETWORK', cause: e }); }
  if (!res.ok) throw new ApiError('prepare', `GET ${url} returned HTTP ${res.status}`, { status: res.status, body: await res.text(), url, method: 'GET' });
  return new Uint8Array(await res.arrayBuffer());
}

export interface CleanupOptions {
  token: string;
  /** Either the Workspace API base URL or the MARA origin to discover it from. */
  workspaceApiUrl?: string;
  maraUrl?: string;
  fetch?: FetchLike;
  storage?: StorageLike;
  liteWorkspaceStorage?: StorageLike;
  signal?: AbortSignal;
}

/** Delete a partially built workspace (DELETE /workspaces/{id}) and forget the persisted progress. */
export async function cleanupPartial(workspaceId: string, options: CleanupOptions): Promise<void> {
  let base = options.workspaceApiUrl;
  if (!base) {
    const infoOpts: Parameters<typeof getMaraInfo>[1] = {};
    if (options.fetch) infoOpts.fetch = options.fetch;
    if (options.signal) infoOpts.signal = options.signal;
    base = (await getMaraInfo(options.maraUrl ?? DEFAULT_MARA_URL, infoOpts)).workspace_api_url;
  }
  const api = createWorkspaceApi({ baseUrl: base, token: options.token, ...(options.fetch ? { fetch: options.fetch } : {}), ...(options.signal ? { signal: options.signal } : {}) });
  await api.deleteWorkspace(workspaceId);
  const storage = options.storage ?? defaultStorage('sessionStorage');
  const saved = readProgress(storage);
  if (saved?.workspaceId === workspaceId) clearProgress(storage);
  forgetLiteWorkspace(workspaceId, options.liteWorkspaceStorage ?? defaultStorage('localStorage'));
}
